import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/hooks/useCart";
import PaymentBadges from "@/components/feature/PaymentBadges";
import { useThemeColor, hexAlpha } from "@/hooks/useThemeColor";

export default function CartDrawer() {
  const { items, totalItems, totalPrice, isOpen, closeCart, removeItem, updateQty, clearCart } = useCart();
  const [clearing, setClearing] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const tc = useThemeColor();

  const goToCheckout = () => {
    closeCart();
    navigate("/checkout");
  };

  // Lock body scroll when open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const handleClear = () => {
    setClearing(true);
    setTimeout(() => {
      clearCart();
      setClearing(false);
    }, 300);
  };

  const sep = "─────────────────";
  const whatsappMessage = encodeURIComponent(
    [
      "🛒 *استفسار عن طلب — متجر الإبداع*",
      sep,
      "📦 *المنتجات:*",
      ...items.map((i) => `  • ${i.nameAr} × ${i.qty}  ←  ${(i.price * i.qty).toLocaleString()} ر.س`),
      sep,
      `💰 *الإجمالي: ${totalPrice.toLocaleString()} ر.س*`,
      "🚚 *الشحن:* مجاني",
      sep,
      "أرغب بإتمام الطلب، أرجو التواصل معي لتأكيد التفاصيل 🙏",
    ].join("\n")
  );

  return (
    <>
      {/* Overlay */}
      <div
        ref={overlayRef}
        onClick={closeCart}
        className={`fixed inset-0 z-50 bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />

      {/* Drawer — slides from right (RTL) */}
      <div
        dir="rtl"
        className={`fixed top-0 right-0 h-full z-50 w-full max-w-md bg-[#0f0f0f] border-l border-white/5 flex flex-col transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-full" style={{ background: hexAlpha(tc.primary, 0.1) }}>
              <i className="ri-shopping-cart-line" style={{ color: tc.primary }}></i>
            </div>
            <div>
              <h2 className="text-white font-bold text-base" style={{ fontFamily: "'Cairo', sans-serif" }}>
                سلة التسوق
              </h2>
              <p className="text-gray-500 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {totalItems} {totalItems === 1 ? "منتج" : "منتجات"}
              </p>
            </div>
          </div>
          <button
            onClick={closeCart}
            className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/5 rounded-full transition-all cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <div className="w-20 h-20 flex items-center justify-center bg-[#1a1a1a] rounded-full mb-5">
                <i className="ri-shopping-cart-line text-4xl text-gray-600"></i>
              </div>
              <h3 className="text-white font-bold text-lg mb-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                السلة فارغة
              </h3>
              <p className="text-gray-500 text-sm mb-6" style={{ fontFamily: "'Cairo', sans-serif" }}>
                أضف منتجات من المتجر وستظهر هنا
              </p>
              <button
                onClick={closeCart}
                className="font-bold px-6 py-3 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap"
                style={{ background: tc.primary, color: tc.textOnPrimary, fontFamily: "'Cairo', sans-serif" }}
                onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.background = tc.primaryLight}
                onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.background = tc.primary}
              >
                تصفح المنتجات
              </button>
            </div>
          ) : (
            <div className={`flex flex-col gap-0 transition-opacity duration-300 ${clearing ? "opacity-0" : "opacity-100"}`}>
              {items.map((item, idx) => (
                <div
                  key={item.id}
                  className={`flex gap-4 px-5 py-5 ${idx < items.length - 1 ? "border-b border-white/5" : ""}`}
                >
                  {/* Image */}
                  <Link
                    to={`/product/${item.id}`}
                    onClick={closeCart}
                    className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-[#1a1a1a] cursor-pointer"
                  >
                    <img
                      src={item.image}
                      alt={item.nameAr}
                      className="w-full h-full object-cover object-top hover:scale-105 transition-transform"
                    />
                  </Link>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <span className="text-xs" style={{ fontFamily: "'Cairo', sans-serif", color: hexAlpha(tc.primary, 0.7) }}>
                      {item.category}
                    </span>
                    <Link
                      to={`/product/${item.id}`}
                      onClick={closeCart}
                      className="block text-white font-semibold text-sm leading-tight mt-0.5 mb-2 truncate transition-colors cursor-pointer"
                      style={{ fontFamily: "'Cairo', sans-serif" }}
                      onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.color = tc.primary}
                      onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.color = 'white'}
                    >
                      {item.nameAr}
                    </Link>

                    {/* تنبيه نفاد المخزون */}
                    {item.stock !== undefined && item.stock <= 0 && (
                      <div className="flex items-center gap-1 mb-2 bg-red-500/10 border border-red-500/20 rounded-lg px-2 py-1">
                        <i className="ri-close-circle-fill text-red-400 text-xs"></i>
                        <span className="text-red-400 text-[11px] font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>نفد المخزون — سيُحذف من السلة</span>
                      </div>
                    )}
                    {item.stock !== undefined && item.stock > 0 && item.qty >= item.stock && (
                      <div className="flex items-center gap-1 mb-2 bg-amber-400/10 border border-amber-400/20 rounded-lg px-2 py-1">
                        <i className="ri-error-warning-fill text-amber-400 text-xs"></i>
                        <span className="text-amber-400 text-[11px] font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>وصلت للحد الأقصى ({item.stock} قطعة)</span>
                      </div>
                    )}

                    <div className="flex items-center justify-between flex-wrap gap-2">
                      {/* Qty control */}
                      {item.stock !== undefined && item.stock <= 0 ? (
                        <span className="text-red-400 text-xs font-bold bg-red-500/10 border border-red-500/20 px-3 py-1.5 rounded-full" style={{ fontFamily: "'Cairo', sans-serif" }}>
                          نفد المخزون
                        </span>
                      ) : (
                        <div className="flex items-center border border-white/10 rounded-full overflow-hidden h-8">
                          <button
                            onClick={() => updateQty(item.id, item.qty - 1)}
                            className="w-8 h-8 flex items-center justify-center text-gray-400 transition-colors cursor-pointer"
                            onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.color = tc.primary}
                            onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.color = ''}
                          >
                            <i className="ri-subtract-line text-xs"></i>
                          </button>
                          <span className="w-7 text-center text-white text-sm font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
                            {item.qty}
                          </span>
                          <button
                            onClick={() => updateQty(item.id, item.qty + 1)}
                            disabled={item.stock !== undefined && item.qty >= item.stock}
                            className="w-8 h-8 flex items-center justify-center text-gray-400 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                            onMouseEnter={(e) => { if (!(item.stock !== undefined && item.qty >= item.stock)) (e.currentTarget as HTMLElement).style.color = tc.primary; }}
                            onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.color = ''}
                          >
                            <i className="ri-add-line text-xs"></i>
                          </button>
                        </div>
                      )}
                      {/* Price */}
                      <span className="font-black text-sm" style={{ fontFamily: "'Cairo', sans-serif", color: item.stock !== undefined && item.stock <= 0 ? '#6b7280' : tc.primary }}>
                        {(item.price * item.qty).toLocaleString()} ر.س
                      </span>
                    </div>
                  </div>

                  {/* Remove */}
                  <button
                    onClick={() => removeItem(item.id)}
                    className="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-red-400 transition-colors flex-shrink-0 self-start mt-1 cursor-pointer rounded-full hover:bg-red-400/10"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-white/5 px-5 py-5 flex flex-col gap-4 bg-[#0a0a0a]">
            {/* Summary */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400" style={{ fontFamily: "'Cairo', sans-serif" }}>عدد المنتجات</span>
                <span className="text-white" style={{ fontFamily: "'Cairo', sans-serif" }}>{totalItems} قطعة</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white font-bold text-base" style={{ fontFamily: "'Cairo', sans-serif" }}>الإجمالي</span>
                <span className="font-black text-xl" style={{ fontFamily: "'Cairo', sans-serif", color: tc.primary }}>
                  {totalPrice.toLocaleString()} ر.س
                </span>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="border border-white/5 rounded-xl p-3 bg-[#111]">
              <p className="text-gray-500 text-[10px] mb-2 flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                <i className="ri-shield-check-line" style={{ color: tc.primary }}></i>
                طرق الدفع المقبولة
              </p>
              <PaymentBadges size="sm" />
            </div>

            {/* Checkout CTA */}
            <button
              onClick={goToCheckout}
              className="flex items-center justify-center gap-2 w-full font-black py-4 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap"
              style={{
                background: tc.primary,
                color: tc.textOnPrimary,
                fontFamily: "'Cairo', sans-serif",
              }}
              onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.background = tc.primaryLight}
              onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.background = tc.primary}
            >
              <i className="ri-secure-payment-line text-lg"></i>
              إتمام الطلب
            </button>

            {/* WhatsApp fallback */}
            <a
              href={`https://wa.me/966541138847?text=${whatsappMessage}`}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center justify-center gap-2 w-full bg-[#111] border border-white/10 hover:border-green-400/40 text-white font-bold py-3 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            >
              <i className="ri-whatsapp-line text-green-400 text-lg"></i>
              أو اطلب عبر واتساب
            </a>

            {/* Clear */}
            <button
              onClick={handleClear}
              className="text-gray-500 hover:text-red-400 text-xs text-center transition-colors cursor-pointer py-1"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            >
              <i className="ri-delete-bin-line ml-1"></i>
              مسح السلة كاملاً
            </button>
          </div>
        )}
      </div>
    </>
  );
}
